function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AuthStateProvider, useAuth } from '@mono/ui-runtime/context';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SessionDebugPanel } from '../components/SessionDebug';
import { SessionExpiredDialog, SessionWarningDialog } from '../components/SessionTimeout';
import { authFetch, broadcastLogin, broadcastLogout, broadcastRefresh, cancelScheduledTimers, clearTokenExpiry, closeAuthChannel, coordinatedRefresh, createAuthChannel, getTimeRemaining, isTokenExpired, needsRefresh, resetAuthFetch, restoreTokenExpiry, setAuthFetchCallbacks, setSessionCallbacks, setSessionExpiredHandler, setTokenExpiry, setupAuthListener } from '../lib/auth';
import { useRemoteCatalog } from '../lib/tool-configs/remote';
import { getSSOConfig } from '../utils/sso';
export { useAuth };
const USER_KEY = 'auth_user'; // Store non-sensitive user data in localStorage
export const RETURN_PATH_KEY = 'auth_return_path';
/** A failed invite completion; code 'invite_invalid' = expired or used link. */ export class InviteError extends Error {
    constructor(message, code){
        super(message), _define_property(this, "code", void 0), this.code = code;
        this.name = 'InviteError';
    }
}
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ssoProviders, setSSOProviders] = useState(null);
    const [ssoLoading, setSSOLoading] = useState(false);
    const [sessionWarning, setSessionWarning] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [authChannel, setAuthChannel] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    // Handle session expiry - show expired banner with login button.
    // Always dismisses the warning dialog first so both dialogs can't be
    // mounted simultaneously (e.g. transient failure near end-of-life followed
    // by the expiry timer firing before the user retries).
    const handleSessionExpired = useCallback(()=>{
        const currentPath = location.pathname;
        if (currentPath === '/login' || currentPath === '/register') {
            clearTokenExpiry();
            return;
        }
        const fullPath = currentPath + location.search + location.hash;
        if (fullPath) {
            sessionStorage.setItem(RETURN_PATH_KEY, fullPath);
        }
        setSessionWarning(false);
        setSessionExpired(true);
    }, [
        location
    ]);
    // Keep a ref so module-level callbacks always call the latest version
    const handleSessionExpiredRef = useRef(handleSessionExpired);
    useEffect(()=>{
        handleSessionExpiredRef.current = handleSessionExpired;
    }, [
        handleSessionExpired
    ]);
    // Handle session warning - show countdown banner (only fires if proactive refresh failed)
    const handleSessionWarning = useCallback(()=>{
        setSessionWarning(true);
    }, []);
    // Refresh the session proactively.
    // The user-triggered "Stay Logged In" path runs after the warning timer has
    // already fired, so the absolute expiry timer is <30s from firing. We must
    // cancel the scheduled timers synchronously BEFORE awaiting the refresh —
    // otherwise the expiry timer can fire mid-refresh and force a logout even
    // when the refresh ultimately succeeds.
    const refreshSession = useCallback(async ()=>{
        // Cancel timers synchronously so the absolute expiry timer can't fire
        // mid-refresh. Do NOT dismiss the warning dialog yet — keep it visible
        // until we know the refresh actually succeeded, so the user retains a
        // retry affordance on transient failures.
        cancelScheduledTimers();
        const outcome = await coordinatedRefresh();
        if (outcome.status === 'ok') {
            // onRefreshSuccess (set in setAuthFetchCallbacks) already called
            // setSessionWarning(false), setSessionExpired(false), and broadcastRefresh.
            return true;
        }
        // 'expired' → the refresh endpoint rejected the cookie; evict immediately.
        // 'transient' → 5xx/network; don't evict — re-arm timers against the
        //   remaining lifetime and leave the warning dialog visible so the user
        //   can click "Stay Logged In" again.
        if (outcome.status === 'expired') {
            setSessionWarning(false);
            handleSessionExpiredRef.current();
        } else {
            const remainingMs = getTimeRemaining();
            if (remainingMs > 0) {
                setTokenExpiry(Math.ceil(remainingMs / 1000));
            }
        }
        return false;
    }, []);
    // Initialize auth channel and token manager on mount
    // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally run only on mount - these callbacks are stable
    useEffect(()=>{
        const channel = createAuthChannel();
        setAuthChannel(channel);
        // 'ok' → onRefreshSuccess handles broadcastRefresh.
        // 'expired' → onRefreshFailure (set below) surfaces the dialog once.
        // 'transient' → leave timers running; warning/expiry will fire as scheduled.
        const refreshInBackground = async ()=>{
            await coordinatedRefresh();
        };
        // The expiry timer (or an overdue timer after sleep / a frozen tab) gets one
        // refresh before the user is told the session is over: another tab may have
        // refreshed, or the cookie may still be good.
        const expireAfterRetry = async ()=>{
            cancelScheduledTimers();
            const outcome = await coordinatedRefresh();
            if (outcome.status !== 'transient') return;
            if (isTokenExpired()) {
                handleSessionExpiredRef.current();
                return;
            }
            const remainingMs = getTimeRemaining();
            if (remainingMs > 0) setTokenExpiry(Math.ceil(remainingMs / 1000));
        };
        // Timers are throttled or frozen while hidden or offline; catch up on return.
        const refreshIfNeeded = ()=>{
            if (document.visibilityState === 'hidden') return;
            if (needsRefresh()) void refreshInBackground();
        };
        document.addEventListener('visibilitychange', refreshIfNeeded);
        window.addEventListener('online', refreshIfNeeded);
        // Set up session callbacks BEFORE restoring expiry, so expired-on-load triggers the banner
        // onRefreshNeeded is inlined to use the local `channel` variable.
        // Using `refreshSession` here would capture stale authChannel state (null at mount).
        setSessionCallbacks({
            onRefreshNeeded: refreshInBackground,
            onSessionWarning: handleSessionWarning,
            onSessionExpired: expireAfterRetry
        });
        // Restore token expiry from localStorage (after callbacks are set so expired tokens trigger banner)
        restoreTokenExpiry();
        // Set up authFetch callbacks
        setAuthFetchCallbacks({
            onRefreshSuccess: (expiresIn)=>{
                setSessionWarning(false);
                setSessionExpired(false);
                broadcastRefresh(channel, expiresIn);
            },
            onRefreshFailure: ()=>{
                handleSessionExpiredRef.current();
            }
        });
        // Register the global session-expired handler for non-timer code paths
        // (e.g. useShanksChat's pre-flight refresh) to surface the expired state.
        setSessionExpiredHandler(()=>handleSessionExpiredRef.current());
        // Set up listener for auth events from other tabs
        const cleanup = setupAuthListener(channel, {
            onLogin: (expiresIn)=>{
                // Another tab logged in, refresh our auth state
                if (expiresIn) {
                    setTokenExpiry(expiresIn);
                }
                checkAuth({
                    showLoading: false
                });
            },
            onLogout: ()=>{
                // Another tab logged out, clear our state
                setUser(null);
                localStorage.removeItem(USER_KEY);
                clearTokenExpiry();
                resetAuthFetch();
                navigate('/login');
            },
            onRefresh: (expiresIn)=>{
                // Another tab refreshed, update our token expiry
                setTokenExpiry(expiresIn);
            }
        });
        return ()=>{
            document.removeEventListener('visibilitychange', refreshIfNeeded);
            window.removeEventListener('online', refreshIfNeeded);
            cleanup?.();
            closeAuthChannel(channel);
            setSessionExpiredHandler(null);
        };
    }, []);
    const checkAuth = useCallback(async ({ showLoading = true } = {})=>{
        try {
            if (showLoading) setIsLoading(true);
            const response = await authFetch('/api/check-auth', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            // Intentional: if authFetch attempts a refresh and gets a transient outcome
            // (5xx/network on /api/auth/refresh), it returns the original 401 from
            // check-auth. We treat any non-ok here as logged-out and clear local state.
            // This is acceptable — by the time check-auth itself returns 401 the access
            // token is already invalid; a transient refresh failure is indistinguishable
            // from an expired session at this boundary.
            if (response.ok) {
                const userData = await response.json();
                /**
         * Maps backend user response to frontend User type.
         * Note: Backend API response uses 'id' (lowercase), not 'userID'.
         * The backend IUser interface uses 'userID', but the actual API response returns 'id'.
         * This mapping ensures frontend type safety with the actual API contract.
         */ const mappedUser = {
                    id: userData.id,
                    email: userData.email,
                    name: userData.name,
                    role: userData.role,
                    tenant_id: userData.tenant_id,
                    company: userData.company
                };
                setUser(mappedUser);
                localStorage.setItem(USER_KEY, JSON.stringify(mappedUser));
            } else {
                setUser(null);
                localStorage.removeItem(USER_KEY);
            }
        } catch  {
            // Auth check failed - user remains unauthenticated
            setUser(null);
            localStorage.removeItem(USER_KEY);
        } finally{
            if (showLoading) setIsLoading(false);
        }
    }, []);
    // Load user data from localStorage on mount, then check auth
    useEffect(()=>{
        const storedUser = localStorage.getItem(USER_KEY);
        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
            } catch (err) {
                console.error('Failed to parse stored user:', err);
                localStorage.removeItem(USER_KEY);
            }
        }
        // Always check auth with server (cookies may have expired)
        checkAuth();
    }, [
        checkAuth
    ]);
    // Re-fetch the agent tool catalog whenever the user becomes authenticated.
    // The boot-time call from main.tsx fires pre-login and gets 401'd by sabaody;
    // without this, tool cards permanently render with the humanized-id fallback.
    const prevUserId = useRef(null);
    useEffect(()=>{
        const currentId = user?.id ?? null;
        if (currentId && currentId !== prevUserId.current) {
            void useRemoteCatalog.getState().refresh();
        }
        prevUserId.current = currentId;
    }, [
        user
    ]);
    const login = async (email, password)=>{
        setError(null);
        try {
            const response = await fetch('/api/login', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email,
                    password
                })
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || errorData.error || 'Login failed');
            }
            acceptSession(await response.json());
            // Redirect to saved path (from session expiry) or default to home
            const returnPath = sessionStorage.getItem(RETURN_PATH_KEY);
            sessionStorage.removeItem(RETURN_PATH_KEY);
            navigate(returnPath || '/');
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Login failed';
            setError(errorMessage);
            throw err;
        }
    };
    // The session a password sign-in answers with (login, completeInvite):
    // tokens are already in httpOnly cookies, so only the user and expiry land.
    const acceptSession = (userData)=>{
        const user = userData.user;
        if (!user) {
            throw new Error('No user data received');
        }
        setUser(user);
        localStorage.setItem(USER_KEY, JSON.stringify(user));
        if (typeof userData.expires_in === 'number') {
            setTokenExpiry(userData.expires_in);
            broadcastLogin(authChannel, userData.expires_in);
        }
        setSessionWarning(false);
        setSessionExpired(false);
    };
    // SAD-953: an emailed invite sets the password and signs in at once. An
    // expired or used link is reported with code 'invite_invalid', which the
    // set-password page shows as its own state.
    const completeInvite = async (token, newPassword)=>{
        const response = await fetch('/api/complete-invite', {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                token,
                new_password: newPassword
            })
        });
        const data = await response.json().catch(()=>({}));
        if (!response.ok) {
            throw new InviteError(data.error || 'Could not set your password', data.code);
        }
        acceptSession(data);
        navigate('/');
    };
    const register = async (email, password, name)=>{
        setError(null);
        try {
            const response = await fetch('/api/register', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email,
                    password,
                    name
                })
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || errorData.error || 'Registration failed');
            }
            const userData = await response.json();
            const user = userData.user;
            if (user) {
                setUser(user);
                localStorage.setItem(USER_KEY, JSON.stringify(user));
                // Set token expiry if provided
                if (typeof userData.expires_in === 'number') {
                    setTokenExpiry(userData.expires_in);
                    broadcastLogin(authChannel, userData.expires_in);
                }
                navigate('/');
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Registration failed';
            setError(errorMessage);
            throw err;
        }
    };
    const logout = async ()=>{
        try {
            setIsLoading(true);
            // Call backend to clear cookies
            await fetch('/api/logout', {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
        } catch (err) {
            console.error('Logout request failed:', err);
        } finally{
            // Clear state regardless of API call success
            setUser(null);
            setError(null);
            localStorage.removeItem(USER_KEY);
            clearTokenExpiry();
            resetAuthFetch();
            broadcastLogout(authChannel);
            setIsLoading(false);
            navigate('/login');
        }
    };
    const clearError = ()=>{
        setError(null);
    };
    const fetchSSOProviders = useCallback(async ()=>{
        setSSOLoading(true);
        try {
            const config = getSSOConfig();
            // Use the public providers endpoint that doesn't require authentication
            const url = `/api/auth/${config.tenantSlug}/providers`;
            const response = await fetch(url, {
                credentials: 'include'
            });
            if (!response.ok) {
                throw new Error('Failed to fetch SSO providers');
            }
            const data = await response.json();
            setSSOProviders(data);
        } catch (err) {
            console.error('Failed to fetch SSO providers:', err);
            // Set default providers if fetch fails
            setSSOProviders(null);
        } finally{
            setSSOLoading(false);
        }
    }, []);
    // Handle login from session expiry banner
    const handleExpiredLogin = useCallback(()=>{
        setSessionWarning(false);
        setSessionExpired(false);
        setUser(null);
        localStorage.removeItem(USER_KEY);
        clearTokenExpiry();
        resetAuthFetch();
        navigate('/login');
    }, [
        navigate
    ]);
    const value = {
        user,
        isLoading,
        isAuthenticated: !!user,
        ssoProviders,
        ssoLoading,
        login,
        register,
        completeInvite,
        logout,
        checkAuth,
        fetchSSOProviders,
        refreshSession,
        error,
        clearError
    };
    return /*#__PURE__*/ _jsxs(AuthStateProvider, {
        value: value,
        children: [
            children,
            /*#__PURE__*/ _jsx(SessionWarningDialog, {
                open: sessionWarning,
                onStayLoggedIn: refreshSession
            }),
            /*#__PURE__*/ _jsx(SessionExpiredDialog, {
                open: sessionExpired,
                onLogin: handleExpiredLogin
            }),
            process.env.NODE_ENV === 'development' && /*#__PURE__*/ _jsx(SessionDebugPanel, {})
        ]
    });
}
// RETIRED (SAD-182): migration 000049_collapse_baseline_roles rewrote every
// view_only user to member, so no session can hold this role any more and
// this always evaluates false for every real session (true only on the
// isLoading fail-safe below). Read/write posture moved to the policy spine;
// this hook is what it replaced. Do not add new call sites. Pending deletion
// once the remaining call sites land in their own PRs: OptionalTasksTab.tsx,
// OptionalTaskExercisesDrawer.tsx (PR #1165), ModificationCenter.tsx
// (PRs #1232, #1243), ProgramCompaniesTab.tsx (PR #1245),
// ProgramContactsTab.tsx (PR #1244), and the orphaned ProgramTasksTab.tsx
// (unrouted — see major-push/7-29-specra/18-retire-useisviewonly.md).
// CustomerTouchpointsTab.tsx converted to canEdit under SAD-163 — it was
// previously listed here as "PR #1166", but #1166 (SAD-177) never touched
// this hook; that credit was inaccurate.
export function useIsViewOnly() {
    const { user, isLoading } = useAuth();
    if (isLoading) return true;
    return user?.role === 'view_only';
}
